import { useUserContext } from "@/context/UserContext";
import { handleImageError } from "@/utils/GalleryFunctions";
import { object, string } from "yup";
import { useFormik } from "formik";
import usePhotos from "@/hooks/usePhotos";
import { usePhotoViewContext } from "@/context/PhotoViewContext";
import { useState } from "react";
import { notifyError, notifySuccess } from "@/utils/NotificationFunctions";
import { readOnlyMessage, suspendedMessage } from "@/utils/DefaultMessages";
import { LogI } from "@/interfaces/Logs";
import { useGalleryContext } from "@/context/GalleryContext";
import { createLog } from "@/services/LogService";

const loginSchema = object({
  discussion: string(),
});

interface NewCommentPropsI {
  photoId: string;
  parentCommentId?: string;
}

function NewDiscussion(props: NewCommentPropsI) {
  const { gallery } = useGalleryContext();
  const { currentUser, userToken } = useUserContext();
  const { newDiscussionInPhoto } = usePhotos();
  const { getDiscussions } = usePhotoViewContext();
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      discussion: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoadingSubmit(true);
      if (props.photoId) {
        await newDiscussionInPhoto(
          {
            comment: values.discussion,
            ...(props.parentCommentId && {
              parentCommentId: props.parentCommentId,
            }),
          },
          props.photoId,
        )
          .then(async (res) => {
            if (res.status === "Success") {
              const log: LogI = {
                type: "Discussion",
                typeNumber: 7,
                visitStart: Date.now(),
                gallery_name: gallery?.name ?? "",
                text: res.data.comment,
              };
              await createLog(userToken, log);
              if (currentUser?.admin === 0) {
                notifySuccess(
                  "Uploaded successfully. The content will be visible after the moderation.",
                );
              }
              getDiscussions();
              resetForm();
            } else {
              notifyError("An error has ocurred");
            }
          })
          .finally(() => {
            setLoadingSubmit(false);
          });
      }
    },
  });

  if (!currentUser)
    return <h3 className="red-text-alert">Log in to write a discussion</h3>;

  if (currentUser && currentUser.readOnly)
    return (
      <h3 className="red-text-alert">
        {readOnlyMessage(
          currentUser.readOnlyEndDate,
          currentUser.readOnlyReason,
        )}
      </h3>
    );

  if (currentUser && currentUser.isSuspended)
    return (
      <h3 className="red-text-alert">
        {suspendedMessage(
          currentUser.suspendedEndDate,
          currentUser.suspendedReason,
        )}
      </h3>
    );

  return (
    <form
      className="new-comment-component-main-container"
      onSubmit={formik.handleSubmit}
    >
      <div className="input-container">
        <img
          src={currentUser.avatar}
          onError={handleImageError}
          alt="user profile avatar"
        />
        <input
          className="font-light"
          type="text"
          id="discussion"
          name="discussion"
          placeholder="Add a discussion"
          value={formik.values.discussion}
          onChange={formik.handleChange}
        />
      </div>
      <button
        className="btn linear-bg"
        type="submit"
        disabled={formik.values.discussion.length === 0 || loadingSubmit}
      >
        Comment
      </button>
    </form>
  );
}

export default NewDiscussion;
